import type { WaitlistStatus } from '@/api/waitlist/waitlistDetailQuery/types';

export type WaitlistUiState =
  | 'active'
  | 'available'
  | 'selected'
  | 'expired'
  | 'removed'
  | 'used'
  | 'rescheduled';

export type WaitlistSlotTime =
  | string
  | {
      id?: string | null;
      slotId?: string | null;
    };

interface GetEffectiveWaitlistUiStateParams {
  status: WaitlistStatus;
  attemptId?: string | null;
  slotId?: string | null;
  slotTimes?: WaitlistSlotTime[] | null;
}

const hasValue = (value?: string | null): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const slotExists = (
  slotTimes: WaitlistSlotTime[] | null | undefined,
  slotId: string,
) =>
  Boolean(
    slotTimes?.some((slot) => {
      if (typeof slot === 'string') {
        return slot === slotId;
      }

      return slot.id === slotId || slot.slotId === slotId;
    }),
  );

export const getEffectiveWaitlistUiState = ({
  status,
  attemptId,
  slotId,
  slotTimes,
}: GetEffectiveWaitlistUiStateParams): WaitlistUiState => {
  if (status === 'removed') {
    return 'removed';
  }

  if (status === 'used') {
    return 'used';
  }

  if (status === 'rescheduled') {
    return 'rescheduled';
  }

  if (status === 'expired') {
    return 'expired';
  }

  // status is null — waitlist is still active
  const hasSlots = (slotTimes?.length ?? 0) > 0;
  const isLocallySelected =
    hasValue(attemptId) && hasValue(slotId) && slotExists(slotTimes, slotId);

  if (hasSlots) {
    return isLocallySelected ? 'selected' : 'available';
  }

  return isLocallySelected ? 'selected' : 'active';
};
